import React, { CSSProperties, useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './Trigger.module.scss';
import { Icon } from '../images/Icon';
import { getColor } from '../../../utils/utils_color';

export interface TriggerProps {
  name?: string;
  onClick: () => void;
  iconName?: string;
  iconPosition?: 'before' | 'after';
  iconWidth?: number;
  iconHeight?: number;
  iconBackgroundColor?: string;
  iconColor?: string;
  iconHoverColor?: string;
  // 'flat' : l'aspect du bouton « Créer un média » de MediaList — angles à 9px,
  // aplat de couleur, texte en 500, léger enfoncement au clic. La géométrie est
  // dans Trigger.module.scss, les couleurs restent celles des props ci-dessous
  // et retombent, à défaut, sur l'accent bleu de ce bouton.
  variant?: 'default' | 'tailwind1' | 'tailwind2' | 'flat' | 'custom';
  classNameNormal?: string;
  classNamePushed?: string;
  isActive?: boolean;
  width?: string;
  height?: string;
  padding?: string;
  margin?: string;
  buttonForegroundColor?          : string;
  buttonBackgroundColor?          : string;
  buttonBackgroundHoverColor?     : string;
  buttonForegroundHoverColor?     : string;
  buttonBackgroundSelectedColor?  : string;
  buttonForegroundSelectedColor?  : string;
  buttonBackgroundDisabledColor?  : string;
  buttonForegroundDisabledColor?  : string;
  toClick?: boolean;
  ackAnimation?: boolean;
  toClickColor?: string;
  ackAnimationColor?: string;
  onAckAnimationFinished?: () => void;
  animationInterval?: number;
  unavailable?: boolean;
  fontSize?: string;
  // Donne le focus clavier au bouton dès son montage (Entrée l'active alors).
  autoFocus?: boolean;
}

export const Trigger = ({
  name,
  onClick,
  iconName,
  iconPosition                  = 'before',
  iconWidth                     = 25,
  iconHeight                    = 25,
  iconBackgroundColor           = "transparent",
  iconColor                     = "var(--color-Button-FG-idle)",
  iconHoverColor                = "var(--color-Button-FG-hover)",
  variant                       = 'default',
  classNameNormal,
  classNamePushed,
  isActive                      = false,
  width,
  height,
  padding,
  margin,
  // Sans valeur par défaut ici : elles dépendent de la variante, et les fixer
  // au moment du destructurage empêcherait de distinguer « le parent n'a rien
  // passé » de « le parent a passé la couleur du thème ». La résolution a donc
  // lieu plus bas, dans palette.
  buttonForegroundColor,
  buttonBackgroundColor,
  buttonBackgroundHoverColor,
  buttonForegroundHoverColor,
  buttonBackgroundSelectedColor,
  buttonForegroundSelectedColor,
  buttonBackgroundDisabledColor,
  buttonForegroundDisabledColor,
  toClick                       = false,
  ackAnimation                  = false,
  toClickColor                  = getColor("colorR"),
  ackAnimationColor             = getColor("colorG"),
  onAckAnimationFinished,
  animationInterval             = 500,
  unavailable                   = false,
  fontSize,
  autoFocus                     = false,
}: TriggerProps) => {

  const [isClicked,             setIsClicked            ] = useState(false);
  const [showAckAnimation,      setShowAckAnimation     ] = useState(false);
  const [currentAnimationColor, setCurrentAnimationColor] = useState(ackAnimationColor);
  const [isHovered,             setIsHovered            ] = useState(false);

  // ── Palette effective ────────────────────────────────────────────────────
  //
  // Ce que le parent passe l'emporte toujours. À défaut, les variantes
  // héritées prennent les couleurs de bouton du thème, et 'flat' celles du
  // bouton « Créer un média » : --c-accent n'est déclarée que sur la racine de
  // certains écrans (MediaList, AdminList…), d'où la valeur de repli, sans
  // laquelle le bouton serait transparent partout ailleurs.
  const isFlat = variant === 'flat';

  const palette = {
    fg         : buttonForegroundColor         ?? (isFlat ? "#FFFFFF"                  : "var(--color-Button-FG-idle)"),
    bg         : buttonBackgroundColor         ?? (isFlat ? "var(--c-accent, #3a7fe8)" : "var(--color-Button-BG-idle)"),
    bgHover    : buttonBackgroundHoverColor    ?? (isFlat ? "#2e6ed4"                  : "var(--color-Button-BG-hover)"),
    fgHover    : buttonForegroundHoverColor    ?? (isFlat ? "#FFFFFF"                  : "var(--color-Button-FG-hover)"),
    bgSelected : buttonBackgroundSelectedColor ?? (isFlat ? "#2e6ed4"                  : "var(--color-Button-BG-selected)"),
    fgSelected : buttonForegroundSelectedColor ?? (isFlat ? "#FFFFFF"                  : "var(--color-Button-FG-selected)"),
    bgDisabled : buttonBackgroundDisabledColor ?? (isFlat ? "var(--c-accent, #3a7fe8)" : "var(--color-Button-BG-disable)"),
    fgDisabled : buttonForegroundDisabledColor ?? (isFlat ? "#FFFFFF"                  : "var(--color-Button-FG-disable)"),
  };

  const handleClick = () => {
    if (unavailable) return;

    if (toClick) {
      if (ackAnimation) {
        setShowAckAnimation(true);
        setCurrentAnimationColor(ackAnimationColor);

        const intervalId = setInterval(() => {
          setCurrentAnimationColor(prevColor =>
            prevColor === ackAnimationColor ? toClickColor : ackAnimationColor
          );
        }, animationInterval);

        setTimeout(() => {
          clearInterval(intervalId);
          setShowAckAnimation(false);
          setIsClicked(false);
          setCurrentAnimationColor(ackAnimationColor);
          if (onAckAnimationFinished) onAckAnimationFinished();
        }, 3000);
      } else {
        setIsClicked(false);
      }
    }
    onClick();
  };

  useEffect(() => {
    if (toClick) setIsClicked(true);
  }, [toClick]);

  // ── Couleurs pour tailwind1 / tailwind2 (style inline direct) ────────
  const getBackgroundColor = () => {
    if (unavailable)                                                       return palette.bgDisabled;
    if (showAckAnimation)                                                  return currentAnimationColor;
    if (toClick && isClicked)                                              return toClickColor;
    if ((variant === 'tailwind1' || variant === 'tailwind2') && isActive)  return palette.bgSelected;
    if ((variant === 'tailwind1' || variant === 'tailwind2') && isHovered) return palette.bgHover;
    return palette.bg;
  };

  const getForegroundColor = () => {
    if (unavailable)                                                       return palette.fgDisabled;
    if ((variant === 'tailwind1' || variant === 'tailwind2') && isActive)  return palette.fgSelected;
    if ((variant === 'tailwind1' || variant === 'tailwind2') && isHovered) return palette.fgHover;
    return palette.fg;
  };

  // ── Couleurs pour default et flat (style inline, hover géré via isHovered) ──
  const getBackgroundColorDefault = () => {
    if (unavailable)   return palette.bgDisabled;
    if (showAckAnimation) return currentAnimationColor;
    if (toClick && isClicked) return toClickColor;
    if (isHovered)     return palette.bgHover;
    if (isActive)      return palette.bgSelected;
    return palette.bg;
  };

  const getForegroundColorDefault = () => {
    if (unavailable)   return palette.fgDisabled;
    if (isHovered)     return palette.fgHover;
    if (isActive)      return palette.fgSelected;
    return palette.fg;
  };

  const getClassName = () => {
    if (unavailable) {
      return classNames(
        'inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full font-bold text-sm uppercase cursor-not-allowed opacity-60',
        'bg-[var(--color-I)] text-[var(--color-6)]'
      );
    }

    switch (variant) {
      case 'tailwind1':
        return classNames(
          'relative inline-flex h-12 overflow-hidden rounded-full p-[1px] focus:outline-none'
        );
      case 'tailwind2':
        return classNames(
          'inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full font-bold text-sm uppercase transition-colors duration-200'
        );
      case 'flat':
        return classNames(styles.flat);
      case 'custom':
        return classNames(isActive ? classNamePushed : classNameNormal);
      case 'default':
      default:
        return classNames(
          styles.trigger,
          isActive ? styles.pushed : styles.normal
        );
    }
  };

  const renderContent = () => {

    if (variant === 'tailwind1') {
      return (
        <>
          <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,var(--color-3)_0%,var(--color-1)_50%,var(--color-3)_100%)]" />
          <span
            className = "inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full px-3 py-1 text-xl font-medium backdrop-blur-3xl gap-2"
            style     = {{
              backgroundColor : getBackgroundColor(),
              color           : getForegroundColor(),
              transition      : 'background-color 0.2s ease, color 0.2s ease'
            }}
          >
            {iconName && iconPosition === 'before' && (
              <Icon name={iconName} width={iconWidth} height={iconHeight}
                backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
            )}
            {name}
            {iconName && iconPosition === 'after' && (
              <Icon name={iconName} width={iconWidth} height={iconHeight}
                backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
            )}
          </span>
        </>
      );
    }

    if (variant === 'tailwind2') {
      return (
        <span
          className = "inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full font-bold text-sm tracking-widest uppercase transition-colors duration-200"
          style     = {{
            backgroundColor : getBackgroundColor(),
            color           : getForegroundColor(),
            transition      : 'background-color 0.2s ease, color 0.2s ease'
          }}
        >
          {iconName && iconPosition === 'before' && (
            <Icon name={iconName} width={iconWidth} height={iconHeight}
              backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
          )}
          {name}
          {iconName && iconPosition === 'after' && (
            <Icon name={iconName} width={iconWidth} height={iconHeight}
              backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
          )}
        </span>
      );
    }

    return (
      <>
        {iconName && iconPosition === 'before' && (
          <Icon name={iconName} width={iconWidth} height={iconHeight}
            backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
        )}
        {name}
        {iconName && iconPosition === 'after' && (
          <Icon name={iconName} width={iconWidth} height={iconHeight}
            backgroundColor={iconBackgroundColor} color={iconColor} hoverColor={iconHoverColor} />
        )}
      </>
    );
  };

  const customStyle = {
    width:   width  || 'auto',
    height:  height || 'auto',
    // 'flat' n'a pas de repli ici : son remplissage (9px 18px) est dans le
    // SCSS, et une valeur en ligne le supplanterait.
    padding: padding || (isFlat ? undefined : (variant === 'tailwind1' || variant === 'tailwind2' ? '1px' : '5px')),
    margin:  margin  || (variant === 'tailwind1' || variant === 'tailwind2' ? '10px' : '0'),

    // ── Variables CSS pour le SCSS (fallback quand pas de couleurs passées) ──
    '--trigger-bg'          : palette.bg,
    '--trigger-fg'          : palette.fg,
    '--trigger-bg-hover'    : palette.bgHover,
    '--trigger-fg-hover'    : palette.fgHover,
    '--trigger-bg-selected' : palette.bgSelected,
    '--trigger-fg-selected' : palette.fgSelected,
    '--trigger-bg-disabled' : palette.bgDisabled,
    '--trigger-fg-disabled' : palette.fgDisabled,

    // ── Pour tailwind1/tailwind2 : couleurs via getBackgroundColor ────────
    ...(variant !== 'tailwind1' && variant !== 'tailwind2' && (toClick || showAckAnimation || unavailable) && {
      backgroundColor: getBackgroundColor()
    }),

    // ── Pour default et flat : style inline avec gestion complète des états ──
    // Prioritaire sur le SCSS mais on garde le translateY(-5px) du SCSS
    ...((variant === 'default' || isFlat) && {
      backgroundColor : getBackgroundColorDefault(),
      color           : getForegroundColorDefault(),
    }),

    transition    : showAckAnimation ? 'none' : 'background-color 0.3s ease, transform 0.2s ease',
    cursor        : unavailable ? 'not-allowed' : 'pointer',
    opacity       : unavailable ? 0.6 : 1,
    pointerEvents : unavailable ? 'none' : 'auto',

    // ── Scale uniquement pour tailwind1 et tailwind2 ──────────────────────
    ...((variant === 'tailwind1' || variant === 'tailwind2') && {
      transform: isHovered ? 'scale(1.05)' : 'scale(1)',
    }),

   // 0.875rem pour 'flat', la taille du bouton d'origine ; 16px ailleurs, comme
   // avant. Sans ce cas, la valeur en ligne écraserait celle du SCSS.
   fontSize: fontSize ?? (isFlat ? '0.875rem' : '16px'),
  } as CSSProperties;

  return (
    <button
      className    = {getClassName()}
      autoFocus    = {autoFocus}
      onClick      = {handleClick}
      style        = {customStyle}
      onMouseEnter = {() => setIsHovered(true)}
      onMouseLeave = {() => setIsHovered(false)}
    >
      {renderContent()}
    </button>
  );
};

export default Trigger;